import { ProjectDetailsTabPath } from "@repo/app-types/helpers/adminAppRoutes";
import type { RouteObject } from "react-router";
import { ProjectNameCrumbLabel } from "../../features/projects/projectNameCrumbLabel.js";
import {
  TrainingChapterDialogRoute,
  TrainingParticipantDialogRoute,
} from "../../features/trainingParticipants/trainingParticipantsTab.js";
import { ProjectDetailsPageContainer } from "../../pages/projectDetailsPageContainer.js";
import {
  ProjectConfigurationTabRoute,
  ProjectDefaultTabRedirect,
  ProjectInsightsTabRoute,
  ProjectInterviewsTabRoute,
  ProjectOverviewTabRoute,
  ProjectParticipantsTabRoute,
} from "../../pages/projectDetailsTabRoutes.js";
import { appPaths } from "../appPaths.js";
import { defineHandle } from "../navigation/appRouteHandle.js";
import { ProjectNavigationMenu } from "../navigation/menu/projectNavigationMenu.js";

/** The project (recruiting/training) detail subtree. */
export function createProjectRoute(): RouteObject {
  return {
    path: appPaths.project.pattern,
    // The container resolves the project and renders the recruiting or
    // training page chrome; the pages feed the tab routes below through
    // their outlet context (which tabs exist is data-driven).
    Component: ProjectDetailsPageContainer,
    handle: defineHandle({
      navigationMenu: <ProjectNavigationMenu />,
      crumbs: ({ params, pathname }) => [
        {
          label: <ProjectNameCrumbLabel projectId={params.projectId} />,
          href: pathname,
        },
      ],
    }),
    children: [
      { index: true, Component: ProjectDefaultTabRedirect },
      {
        path: `${ProjectDetailsTabPath.Overview}/*`,
        Component: ProjectOverviewTabRoute,
        handle: defineHandle({ crumbs: () => [{ label: "Overview" }] }),
      },
      {
        path: `${ProjectDetailsTabPath.ProjectConfiguration}/*`,
        Component: ProjectConfigurationTabRoute,
        handle: defineHandle({
          crumbs: () => [{ label: "Configuration" }],
        }),
      },
      {
        path: ProjectDetailsTabPath.Participants,
        Component: ProjectParticipantsTabRoute,
        handle: defineHandle({ crumbs: () => [{ label: "Participants" }] }),
        children: [
          {
            path: "chapters/:chapterId/*",
            Component: TrainingChapterDialogRoute,
          },
          {
            path: ":participantId/*",
            Component: TrainingParticipantDialogRoute,
          },
        ],
      },
      {
        path: `${ProjectDetailsTabPath.Interviews}/:interviewId?/*`,
        Component: ProjectInterviewsTabRoute,
        handle: defineHandle({ crumbs: () => [{ label: "Interviews" }] }),
      },
      {
        path: `${ProjectDetailsTabPath.Insights}/*`,
        Component: ProjectInsightsTabRoute,
        handle: defineHandle({ crumbs: () => [{ label: "Insights" }] }),
      },
      { path: "*", Component: ProjectDefaultTabRedirect },
    ],
  };
}
